import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import InfoIcon from "@mui/icons-material/Info";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import RecyclingIcon from "@mui/icons-material/Recycling";
import { Grid, IconButton, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import useDialog from "../../../hooks/useDialog";
import AddEditDocsRestrictedDialog from "../../dialog/AddEditDocsRestrictedDialog";
import ConfirmationDialog from "../../dialog/ConfirmationDialog/confirmationDialog";
import ModeEditOutlineOutlined from "@mui/icons-material/ModeEditOutlineOutlined";
import RevisionHistoryButton from "./RevisionHistoryButton";

function FileTableCell(props) {
    const { t } = useTranslation();

    const isShowPreview =
        props.file.docs_content_type === "application/pdf" ||
        props.file.content_type === "application/pdf";
    const canBeRemoved = !props.readOnly;
    const miniatureUrl = props.file.id ? props.file.miniature_download_link : props.file?.blob_url;

    const [isRevisionMode, setIsRevisionMode] = useState(false);

    const getFileName = () => {
        switch (props.fileType) {
            case "docs":
                return props.file.docs_name;
            default:
                return props.file.file_name;
        }
    };

    const fileName = getFileName();

    const [openDetailsDialog, onOpenDetailsDialog, onCloseDetailsDialog] = useDialog();

    const [openConfimartionDialog, onOpenConfimartionDialog, onCloseConfimartionDialog] =
        useDialog();

    const downloadFile = useCallback(
        (e) => {
            e.stopPropagation();

            props.onDownload(props.file.id, fileName, props.index);
        },
        [props.file, props.fileType, props.index],
    );

    const previewFile = useCallback(
        (e) => {
            e.stopPropagation();
            props.onPreview(props.file.id, props.index);
        },
        [props.file, props.fileType, props.index],
    );

    const deleteFile = useCallback(
        (e) => {
            e.stopPropagation();
            props.onDeleteFile(props.file.id);
            onCloseConfimartionDialog();
        },
        [props.file, props.index],
    );

    const onOpenRevisionDialog = useCallback((e) => {
        e.stopPropagation();
        setIsRevisionMode(true);
        onOpenDetailsDialog();
    }, []);

    const onOpenEditDialog = useCallback((e) => {
        e.stopPropagation();
        setIsRevisionMode(false);
        onOpenDetailsDialog();
    }, []);

    const onCloseAddEditFileDialog = useCallback(() => {
        if (props.fileRefetch) {
            props.fileRefetch();
        }
        setIsRevisionMode(false);
        onCloseDetailsDialog();
    }, [props.fileRefetch]);

    return (
        <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            columnSpacing={1}
            style={{ minWidth: "max-content" }}
        >
            {props.showDetailsButton ? (
                <Grid item>
                    <Tooltip title={t("other.file_table_cell.details")}>
                        <IconButton
                            color="primary"
                            onClick={onOpenDetailsDialog}
                            sx={{ padding: "5px" }}
                        >
                            <InfoIcon fontSize="medium" />
                        </IconButton>
                    </Tooltip>
                </Grid>
            ) : (
                <Grid item sx={{ width: 36 }} /> // Fixed width placeholder
            )}
            <Grid item>
                {props.showEditButton ? (
                    <Tooltip title={t("other.file_table_cell.edit")}>
                        <IconButton
                            color="primary"
                            onClick={onOpenEditDialog}
                            sx={{ padding: "5px" }}
                        >
                            <ModeEditOutlineOutlined fontSize="medium" />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <div style={{ width: 36, height: 44 }} /> // Fixed size placeholder
                )}
            </Grid>
            <Grid item>
                {props.showRevisionHistoryButton ? (
                    <Tooltip title={t("other.file_table_cell.revision_history")}>
                        <RevisionHistoryButton docsId={props.file.id}/>
                    </Tooltip>
                ) : (
                    <div style={{ width: 0, height: 44 }} /> // Fixed size placeholder
                )}
            </Grid>

            <Grid item>
                <Tooltip title={t("other.file_table_cell.download")}>
                    <IconButton color="primary" onClick={downloadFile} sx={{ padding: "5px" }}>
                        <DownloadIcon fontSize="medium" />
                    </IconButton>
                </Tooltip>
            </Grid>
            <Grid item>
                {isShowPreview ? (
                    <Tooltip title={t("other.file_table_cell.preview")}>
                        <IconButton color="primary" onClick={previewFile} sx={{ padding: "5px" }}>
                            <PreviewOutlinedIcon fontSize="medium" />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <div style={{ width: 36, height: 44 }} /> // Fixed size placeholder
                )}
            </Grid>

            <Grid item>
                {canBeRemoved ? (
                    <Tooltip title={t("other.file_table_cell.delete")}>
                        <IconButton
                            color="primary"
                            onClick={onOpenConfimartionDialog}
                            sx={{ padding: "5px" }}
                        >
                            <DeleteIcon fontSize="medium" />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <div style={{ width: 36, height: 44 }} /> // Fixed size placeholder
                )}
            </Grid>

            <Grid item>
                {props.showRevisionButton ? (
                    <Tooltip title={t("other.file_table_cell.revision")}>
                        <IconButton
                            color="primary"
                            onClick={onOpenRevisionDialog}
                            sx={{ padding: "5px" }}
                        >
                            <RecyclingIcon fontSize="medium" />
                        </IconButton>
                    </Tooltip>
                ) : (
                    <div style={{ width: 36, height: 44 }} /> // Fixed size placeholder
                )}
            </Grid>

            {openDetailsDialog && props.fileType === "docs" && (
                <AddEditDocsRestrictedDialog
                    open={openDetailsDialog}
                    onClose={onCloseAddEditFileDialog}
                    docsId={props.file.id}
                    docInvestId={props.file.docinvest_id}
                    docsStyleId={props.file.docsstyle_id}
                    docsWbsId={props.file.docswbs_id}
                    submitButtonLabel={t("dialog.file_dialog.edit_docs")}
                    readOnly={props.readOnly}
                    onSubmit={onCloseAddEditFileDialog}
                    isRevisionMode={isRevisionMode}
                    product_ids={props.file.related_products}
                    invest_ids={props.file.related_invests}
                    multiple={props.multiple && !isRevisionMode}
                    availableDocsRestrictedTypes={props.availableDocsRestrictedTypes}
                    showStandards={props.showStandards}
                    customer_id={props.customer_id}
                    agreement_id={props.agreement_id}
                    isCardJpg={props.isCardJpg}
                    isCardPdf={props.isCardPdf}
                    isProspectus={props.isProspectus}
                    channel_id={props.channel_id}
                />
            )}
            {openConfimartionDialog && (
                <ConfirmationDialog
                    open={openConfimartionDialog}
                    onYes={deleteFile}
                    onNo={onCloseConfimartionDialog}
                    title={t(`other.file_data_row.remove_${props.fileType}`)}
                    content={t(`other.file_data_row.are_you_sure_to_remove_${props.fileType}`)}
                    maxWidth={"xs"}
                    titleSx={{ color: "var(--alert)" }}
                    contentSx={{ fontFamily: "Roboto", textAlign: "center" }}
                    btnYesColor={"error"}
                    btnNoColor={"secondary"}
                    showDialogContentDividers={false}
                />
            )}
        </Grid>
    );
}

FileTableCell.propTypes = {
    file: PropTypes.object,
    fileType: PropTypes.oneOf(["docs", "file"]),
    readOnly: PropTypes.bool,
    showDetailsButton: PropTypes.bool,
    showRevisionHistoryButton: PropTypes.bool,
    onDownload: PropTypes.func,
    onPreview: PropTypes.func,
    onUpdateFile: PropTypes.func,
    onDeleteFile: PropTypes.func,
    showRevisionButton: PropTypes.bool,
    showEditButton: PropTypes.bool,
    showStandards: PropTypes.bool,
    customer_id: PropTypes.string,
    agreement_id: PropTypes.string,
};

FileTableCell.defaultProps = {
    readOnly: true,
    fileType: "docs",
    showDetailsButton: true,
    showRevisionButton: false,
    showEditButton: false,
    showStandards: false,
    showRevisionHistoryButton: false,
};

export default FileTableCell;
